/**
 * Съёмка у стенда.
 *
 * Заход к поставщику — это сессия. Всё снятое за один заход связано между
 * собой, и комментарий можно оставить трёх охватов:
 *   к камню   — «этот греный»            (кнопка записи после кадра)
 *   к стенду  — «готов уступить десять»  (кнопка «о стенде»)
 *   к выбранным — на экране поставщика, где видно все кадры разом
 *
 * Один и тот же большой круг ведёт через весь цикл:
 *   кадр → диктовка → сохранить → следующий камень.
 */
import { useEffect, useRef, useState } from 'react';
import { db, enqueue, seedTags, tagId, type LocalSupplier, type LocalExhibition, type LocalTag } from '../db';
import { apiFetch, deviceCode, syncNow } from '../sync';
import { grabFrame, openCamera, VoiceRecorder, keepAwake } from '../lib/media';
import { whereIs } from '../lib/where';
import { ulid, localNo, ATTRIBUTES, parseMoney, formatMoney, type AttributeKey } from '@core';

type Mode = 'live' | 'framed' | 'recording' | 'working' | 'ready';
type Scope = 'stone' | 'session';

interface Extracted { attribute: string; value: string; unit?: string; span?: [number, number] }

interface Props {
  supplier: LocalSupplier;
  exhibition: LocalExhibition;
  sessionId: string;
  kind: 'stone' | 'parcel';
  onClose: () => void;
  onOpenStone: (id: string) => void;
  onScanCert: (stoneId: string) => void;
}

export default function Capture({ supplier, exhibition, sessionId, kind, onClose, onOpenStone, onScanCert }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorder = useRef(new VoiceRecorder());
  const wakeOff = useRef<() => void>(() => {});
  const scopeRef = useRef<Scope>('stone');

  const [mode, setMode] = useState<Mode>('live');
  const [error, setError] = useState('');
  const [shotUrl, setShotUrl] = useState('');
  const [stoneId, setStoneId] = useState('');
  const [stoneNo, setStoneNo] = useState('');
  const [nextNo, setNextNo] = useState('');
  const [transcript, setTranscript] = useState('');
  const [facts, setFacts] = useState<Extracted[]>([]);
  const [status, setStatus] = useState('viewed');
  const [seconds, setSeconds] = useState(0);
  const [frames, setFrames] = useState(0);
  const [shotTotal, setShotTotal] = useState(0);
  // Почему текста нет: сети не было или расшифровка не удалась. Это разные
  // беды, и валить всё на сеть — значит врать в глаза человеку со связью.
  const [noteProblem, setNoteProblem] = useState('');
  const [pricing, setPricing] = useState(false);

  // Словарь видов и то, что выбрано на этом кадре. Камней в кадре может быть
  // несколько — значит и тегов несколько.
  const [tags, setTags] = useState<LocalTag[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Расшифровка правится руками: машина слышит хуже человека, а исправлять
  // проще, чем диктовать заново.
  const [voiceId, setVoiceId] = useState('');
  const [draft, setDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Второй кадр того же камня: показываем живую камеру, а не снимаем вслепую.
  const [addingFrame, setAddingFrame] = useState(false);
  const prevShot = useRef('');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const stream = await openCamera();
        if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        wakeOff.current = await keepAwake();
      } catch (e: any) {
        setError(e?.name === 'NotAllowedError'
          ? 'Камера запрещена. Разрешите доступ в настройках сайта и откройте экран заново.'
          : `Камера недоступна: ${e?.message ?? e}`);
      }
    })();
    return () => {
      dead = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      recorder.current.cancel();
      wakeOff.current();
    };
  }, []);

  useEffect(() => () => { if (shotUrl) URL.revokeObjectURL(shotUrl); }, [shotUrl]);

  useEffect(() => {
    void (async () => {
      const code = await deviceCode();
      setNextNo(localNo(exhibition.code, supplier.code, code, supplier.seq + 1));
      setShotTotal(await db.stones.where('sessionId').equals(sessionId).count());
    })();
  }, [exhibition.code, supplier.code, supplier.seq, sessionId]);

  useEffect(() => { void seedTags().then((t) => setTags(t.filter((x) => !x.hidden))); }, []);

  useEffect(() => {
    if (mode !== 'recording') return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [mode]);

  // ── кадр ──────────────────────────────────────────────────────────────────
  async function shoot() {
    if (!videoRef.current) return;
    try {
      const { full, preview } = await grabFrame(videoRef.current);

      const id = ulid();
      const code = await deviceCode();
      const seq = supplier.seq + 1;
      const no = localNo(exhibition.code, supplier.code, code, seq);
      const now = new Date().toISOString();

      await db.suppliers.update(supplier.id, { seq });
      const stone = {
        id, sessionId, localNo: no, kind, supplierId: supplier.id,
        exhibitionId: exhibition.id, status: 'viewed', capturedAt: now, createdAt: now,
      };
      await db.stones.put(stone);
      await enqueue('stone.upsert', stone, ulid());

      await db.media.put({ id: ulid(), subjectId: id, role: 'preview', blob: preview,
        mime: preview.type, bytes: preview.size, uploaded: false, createdAt: now });
      await db.media.put({ id: ulid(), subjectId: id, role: 'photo', blob: full,
        mime: full.type, bytes: full.size, uploaded: false, createdAt: now });

      setStoneId(id); setStoneNo(no); setFrames(1);
      setPicked([]); setVoiceId(''); setDraft(''); setNoteProblem('');
      setShotTotal((n) => n + 1);
      setShotUrl(URL.createObjectURL(preview));
      setNextNo(localNo(exhibition.code, supplier.code, code, seq + 1));
      setMode('framed');
      void syncNow();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }

  /** Ещё кадр того же камня: с другой стороны, на просвет, крупнее. */
  function beginFrame() {
    if (!stoneId) return;
    prevShot.current = shotUrl;
    setShotUrl('');
    setAddingFrame(true);
  }

  function cancelFrame() {
    setShotUrl(prevShot.current);
    setAddingFrame(false);
  }

  async function addFrame() {
    if (!videoRef.current || !stoneId) return;
    try {
      const { full, preview } = await grabFrame(videoRef.current);
      const now = new Date().toISOString();
      await db.media.put({ id: ulid(), subjectId: stoneId, role: 'photo', blob: full,
        mime: full.type, bytes: full.size, uploaded: false, createdAt: now });
      await db.media.put({ id: ulid(), subjectId: stoneId, role: 'preview', blob: preview,
        mime: preview.type, bytes: preview.size, uploaded: false, createdAt: now });
      setFrames((n) => n + 1);
      if (prevShot.current) URL.revokeObjectURL(prevShot.current);
      prevShot.current = '';
      setShotUrl(URL.createObjectURL(preview));
      setAddingFrame(false);
      void syncNow();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }

  // ── диктовка ──────────────────────────────────────────────────────────────
  async function startRec(scope: Scope) {
    try {
      scopeRef.current = scope;
      setSeconds(0);
      setTranscript(''); setFacts([]); setNoteProblem('');
      await recorder.current.start();
      setMode('recording');
    } catch (e: any) {
      setError(e?.name === 'NotAllowedError'
        ? 'Микрофон запрещён. Разрешите доступ в настройках сайта.'
        : `Микрофон недоступен: ${e?.message ?? e}`);
    }
  }

  async function stopRec() {
    setMode('working');
    const out = await recorder.current.stop();
    const scope = scopeRef.current;
    const subject = scope === 'session' ? sessionId : stoneId;
    if (!out || !subject) { setMode(stoneId ? 'framed' : 'live'); return; }

    const mediaId = ulid();
    const voiceId = ulid();
    const now = new Date().toISOString();
    if (scope === 'stone') setVoiceId(voiceId);

    // Аудио сохраняется всегда и остаётся источником истины: расшифровку
    // можно переделать, запись — нет.
    await db.media.put({ id: mediaId, subjectId: subject, role: 'audio', blob: out.blob,
      mime: out.mime, bytes: out.blob.size, uploaded: false, createdAt: now });
    await db.voices.put({ id: voiceId, subjectId: subject, subjectKind: scope,
      mediaId, status: 'pending', durationMs: out.durationMs, createdAt: now });
    await enqueue('voice.add', { id: voiceId, subjectId: subject, subjectKind: scope,
      mediaId, durationMs: out.durationMs }, ulid());

    if (!navigator.onLine) {
      setNoteProblem('offline');
    } else {
      try {
        const fd = new FormData();
        fd.append('file', out.blob, `note.${out.mime.includes('mp4') ? 'mp4' : 'webm'}`);
        const tr = await apiFetch('/api/transcribe', { method: 'POST', body: fd });
        if (!tr.ok) setNoteProblem(tr.status === 401 ? 'auth' : tr.status === 402 ? 'credits' : 'server');
        if (tr.ok) {
          const { text } = (await tr.json()) as { text: string };
          setTranscript(text);
          setDraft(text);
          await db.voices.update(voiceId, { transcript: text, status: 'done' });
          await enqueue('voice.add', { id: voiceId, subjectId: subject, subjectKind: scope, mediaId, transcript: text }, ulid());

          // Поля извлекаем только из заметки о камне. Реплика о стенде —
          // это про поставщика, и раскладывать её по характеристикам нельзя.
          if (scope === 'stone') {
            const ex = await apiFetch('/api/extract', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ transcript: text }),
            });
            if (ex.ok) {
              const { facts: got } = (await ex.json()) as { facts: Extracted[] };
              setFacts(got);
              for (const f of got) {
                const num = Number(String(f.value).replace(/[^\d.-]/g, ''));
                const fact = {
                  id: ulid(), subjectType: 'stone', subjectId: subject, attribute: f.attribute,
                  valueText: String(f.value), valueNum: Number.isFinite(num) ? num : null,
                  unit: f.unit ?? null, sourceType: 'ai_extraction', confidence: 'low',
                  sourceRef: { voiceNoteId: voiceId, span: f.span ?? null },
                  needsReview: true, recordedAt: new Date().toISOString(),
                };
                await db.facts.put(fact as never);
                await enqueue('fact.add', fact, ulid());
              }
            }
          }
        }
      } catch {
        // Расшифровка не удалась — аудио на месте, сервер разберёт позже.
        setNoteProblem('server');
      }
    }

    setMode(scope === 'session' ? (stoneId ? 'framed' : 'live') : 'ready');
    void syncNow();
  }

  async function nextStone() {
    if (stoneId && status !== 'viewed') {
      await db.stones.update(stoneId, { status });
      await enqueue('status.set', { stoneId, status }, ulid());
    }
    if (shotUrl) URL.revokeObjectURL(shotUrl);
    setShotUrl(''); setStoneId(''); setStoneNo(''); setTranscript('');
    setFacts([]); setStatus('viewed'); setSeconds(0); setFrames(0); setNoteProblem('');
    setPicked([]); setVoiceId(''); setDraft('');
    setMode('live');
    void syncNow();
  }

  async function mark(s: string) {
    const next = status === s ? 'viewed' : s;
    setStatus(next);
    if (stoneId) {
      await db.stones.update(stoneId, { status: next });
      await enqueue('status.set', { stoneId, status: next }, ulid());
    }
  }

  // ── теги ──────────────────────────────────────────────────────────────────
  //
  // Вид камня, выбранный человеком, — это его определение, а не слова
  // поставщика и не догадка модели. Поэтому источник «геммолог»: он старше
  // распознанного из речи и перебивает ошибку разбора.
  useEffect(() => {
    if (!stoneId) return;
    const chosen = tags.filter((t) => picked.includes(t.id)).map((t) => t.label);
    const t = setTimeout(() => void writeTags(chosen), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, stoneId]);

  async function writeTags(labels: string[]) {
    if (!stoneId) return;
    const now = new Date().toISOString();

    // Прежнее значение убираем только у себя: на сервере оно останется
    // в истории — видно, что человек передумал, и когда.
    const old = await db.facts.where('subjectId').equals(stoneId)
      .filter((f) => f.attribute === 'stone_type' && f.sourceType === 'gemologist').toArray();
    await db.facts.bulkDelete(old.map((f) => f.id));
    if (!labels.length) return;

    const fact = {
      id: ulid(), subjectType: 'stone', subjectId: stoneId, attribute: 'stone_type',
      valueText: labels.join(' · '), valueNum: null, unit: null,
      sourceType: 'gemologist', confidence: 'assessed',
      sourceRef: { tags: labels }, needsReview: false, recordedAt: now,
    };
    await db.facts.put(fact as never);
    await enqueue('fact.add', fact, ulid());
    void syncNow();
  }

  async function addTag() {
    const label = newTag.trim();
    if (!label) return;
    const id = tagId(label);
    const row = { id, label, sort: 5, hidden: false, createdAt: new Date().toISOString() };
    await db.tags.put(row);
    await enqueue('tag.upsert', row, ulid());
    setTags(await db.tags.filter((t) => !t.hidden).sortBy('sort'));
    setPicked((p) => (p.includes(id) ? p : [...p, id]));
    setNewTag(''); setAdding(false);
    void syncNow();
  }

  // ── правка расшифровки ────────────────────────────────────────────────────
  //
  // Машина слышит хуже человека, особенно названия и числа. Исправленный
  // текст — новая правда: заметка переписывается, поля из неё извлекаются
  // заново, а прежние снимаются, чтобы в карточке не остались обе версии.
  /**
   * Цена, вписанная кнопками.
   *
   * У стенда цену не всегда удобно произносить вслух: рядом стоит продавец.
   * Три касания вместо диктовки. Источник — supplier_claim: это по-прежнему
   * слова поставщика, просто записанные рукой, а не голосом, и потому в них
   * больше уверенности, чем в разборе речи.
   */
  async function savePrice(attr: string, value: number, currency: string) {
    if (!stoneId || !(value > 0)) return;
    const now = new Date().toISOString();
    const mk = (attribute: string, valueText: string, valueNum: number | null) => ({
      id: ulid(), subjectType: 'stone', subjectId: stoneId, attribute,
      valueText, valueNum, unit: null,
      sourceType: 'supplier_claim', confidence: 'claimed',
      sourceRef: { typed: true }, needsReview: false, recordedAt: now,
    });

    const price = mk(attr, String(value), value);
    // Валюта — отдельным фактом, иначе цена в батах покажется долларами.
    const cur = mk('currency', currency, null);
    for (const f of [price, cur]) {
      await db.facts.put(f as never);
      await enqueue('fact.add', f, ulid());
    }

    setFacts((prev) => [...prev,
      { attribute: attr, value: String(value) } as Extracted,
      { attribute: 'currency', value: currency } as Extracted]);
    setPricing(false);
    void syncNow();
  }

  async function saveNote() {
    if (!voiceId || !stoneId) return;
    setSavingNote(true);
    const text = draft.trim();
    try {
      await db.voices.update(voiceId, { transcript: text, status: 'done' });
      await enqueue('voice.add', { id: voiceId, subjectId: stoneId, subjectKind: 'stone', transcript: text }, ulid());
      setTranscript(text);

      const old = await db.facts.where('subjectId').equals(stoneId)
        .filter((f) => f.sourceType === 'ai_extraction').toArray();
      await db.facts.bulkDelete(old.map((f) => f.id));

      if (navigator.onLine && text) {
        const ex = await apiFetch('/api/extract', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript: text }),
        });
        if (ex.ok) {
          const { facts: got } = (await ex.json()) as { facts: Extracted[] };
          setFacts(got);
          for (const f of got) {
            const num = Number(String(f.value).replace(/[^\d.-]/g, ''));
            const fact = {
              id: ulid(), subjectType: 'stone', subjectId: stoneId, attribute: f.attribute,
              valueText: String(f.value), valueNum: Number.isFinite(num) ? num : null,
              unit: f.unit ?? null, sourceType: 'ai_extraction', confidence: 'low',
              sourceRef: { voiceNoteId: voiceId, span: f.span ?? null, edited: true },
              needsReview: true, recordedAt: new Date().toISOString(),
            };
            await db.facts.put(fact as never);
            await enqueue('fact.add', fact, ulid());
          }
        }
      }
      void syncNow();
    } finally {
      setSavingNote(false);
    }
  }

  function press() {
    if (addingFrame) { void addFrame(); return; }
    if (mode === 'live') void shoot();
    else if (mode === 'framed') void startRec('stone');
    else if (mode === 'recording') void stopRec();
    else if (mode === 'ready') void nextStone();
  }

  const clock = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  const hint =
    addingFrame ? 'Снимите тот же камень ещё раз'
    : mode === 'live' ? (kind === 'parcel' ? 'Снимите партию' : 'Наведите и снимите камень')
    : mode === 'framed' ? 'Нажмите, чтобы надиктовать'
    : mode === 'recording' ? `Запись ${clock} — нажмите, чтобы остановить`
    : mode === 'working' ? 'Расшифровываю…'
    : 'Сохранить и снимать следующий';

  const framed = Boolean(shotUrl);
  const noteChanged = draft.trim() !== transcript.trim();

  return (
    <div className={framed ? 'cam cam-work' : 'cam'}>
      <div className="lens">
        <video ref={videoRef} playsInline muted autoPlay style={{ display: shotUrl ? 'none' : 'block' }} />
        {shotUrl && <img className="frozen" src={shotUrl} alt="снятый камень" />}
        {mode === 'live' && !error && <div className="guides"><i /><i /><i /><i /></div>}

        <div className="lenstop">
          <span className="glass" style={{ flex: 1 }}>
            {addingFrame
              ? `Ещё кадр камня ${stoneNo.split('-').slice(-1)[0]}`
              : [supplier.companyName, whereIs(supplier)].filter(Boolean).join(' · ')}
          </span>
          <span className="glass count">{stoneNo ? stoneNo.split('-').slice(-1)[0] : `${shotTotal} снято`}</span>
          <button className="glass round" onClick={addingFrame ? cancelFrame : onClose}
                  aria-label={addingFrame ? 'Отменить кадр' : 'Закончить у стенда'}>✕</button>
        </div>

        {!framed && (
          <div className="readout">
            {error && <div className="panel">{error}</div>}
            {mode === 'live' && !error && stoneNo === '' && (
              <button className="panel" style={{ textAlign: 'left' }} onClick={() => void startRec('session')}>
                <div className="cap">Заметка обо всём стенде</div>
                Скидки, условия, обещания — то, что не про конкретный камень
              </button>
            )}
          </div>
        )}
      </div>

      {/* Под кадром — то, что к нему относится: какие это камни и что сказано.
          Всё на одном экране, чтобы не терять кадр из виду. */}
      {framed && (
        <>
        <div className="tagrow">
            {tags.map((t) => (
              <button key={t.id} className="tagpill" aria-pressed={picked.includes(t.id)}
                      onClick={() => setPicked((p) =>
                        p.includes(t.id) ? p.filter((x) => x !== t.id) : [...p, t.id])}>
                {t.label}
              </button>
            ))}
            <button className="tagpill add" onClick={() => setAdding(true)} aria-label="Новый вид">＋</button>
        </div>

        <div className="work">
          {error && <div className="wcard warn">{error}</div>}

          {mode === 'recording' && (
            <div className="wcard rec">
              <span className="dot" />
              Идёт запись · {clock} — нажмите круг, чтобы остановить
            </div>
          )}

          {mode === 'working' && (
            <div className="wcard">
              <div className="skeleton" style={{ height: 15, marginBottom: 7 }} />
              <div className="skeleton" style={{ height: 15, width: '72%' }} />
            </div>
          )}

          {mode === 'framed' && !voiceId && !error && (
            <div className="wcard quiet">
              {kind === 'parcel' ? (
                <>
                  Партию не снимают целиком: хватит двух-трёх кадров на разброс —
                  по огранке, размеру и цвету. Нажмите круг и скажите общий вес
                  лота, сколько камней и цену: за карат с выбором и лотом.
                </>
              ) : (
                <>
                  Нажмите круг и скажите про камень: вид, вес, происхождение, цена.
                  Текст появится здесь — его можно будет поправить.
                </>
              )}
            </div>
          )}

          {voiceId && mode !== 'recording' && mode !== 'working' && (
            <div className="wcard note">
              <div className="cap">Заметка о камне</div>
              <textarea value={draft} rows={Math.min(8, Math.max(3, Math.ceil(draft.length / 38)))}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Здесь появится расшифровка — её можно править" />
              <div className="noteacts">
                <button className="btn btn-secondary btn-sm" onClick={() => void startRec('stone')}>
                  Надиктовать заново
                </button>
                {noteChanged && (
                  <button className="btn btn-primary btn-sm" disabled={savingNote}
                          onClick={() => void saveNote()}>
                    {savingNote ? 'Сохраняю…' : 'Сохранить правку'}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="wacts">
            {/* Тот самый недостающий кадр: камень снимают с двух сторон и на
                просвет. Раньше на это была только кнопка сертификата, и вторые
                виды уходили в отчёты. */}
            <button className="btn btn-secondary btn-sm" disabled={!stoneId || mode === 'recording'}
                    onClick={beginFrame}>
              Ещё кадр{frames > 1 ? ` · ${frames}` : ''}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={!stoneId || mode === 'recording'}
                    onClick={() => stoneId && onScanCert(stoneId)}>
              Сертификат
            </button>
            <button className="btn btn-secondary btn-sm" disabled={!stoneId || mode === 'recording'}
                    onClick={() => setPricing(true)}>
              Цена
            </button>
            <button className="btn btn-secondary btn-sm" disabled={!stoneId}
                    onClick={() => stoneId && onOpenStone(stoneId)}>
              Карточка камня
            </button>
          </div>

          {facts.length > 0 && (
            <div className="chips dark">
              {facts.map((f, i) => (
                <span className="chip" key={i} style={{ animationDelay: `${i * 30}ms` }}>
                  {ATTRIBUTES[f.attribute as AttributeKey]?.label ?? f.attribute}
                  <b>{f.value}{f.unit ? ` ${f.unit}` : ''}</b>
                </span>
              ))}
            </div>
          )}

          {mode === 'ready' && !transcript && (
            <div className="wcard warn">
              <div className="cap">
                {noteProblem === 'offline' ? 'Без сети'
                 : noteProblem === 'auth' ? 'Нужен вход'
                 : noteProblem === 'credits' ? 'Счёт модели пуст'
                 : noteProblem === 'server' ? 'Расшифровка не удалась'
                 : 'Ничего не разобрано'}
              </div>
              {noteProblem === 'offline'
                ? 'Заметка записана и стоит в очереди. Поля заполнятся, когда появится связь.'
                : noteProblem === 'auth'
                ? 'Заметка записана. Войдите по номеру — и она разберётся.'
                : noteProblem === 'credits'
                ? 'Заметка записана и цела. Расшифровка вернётся, когда пополнят счёт модели, — и разберётся сама. Снимать можно как обычно.'
                : noteProblem === 'server'
                ? 'Заметка записана и сохранена, разбор повторится сам. Аудио не потеряно.'
                : 'В записи не разобрано ни слова. Аудио сохранено — можно надиктовать заново.'}
            </div>
          )}
        </div>
        </>
      )}

      {adding && (
        <>
          <div className="backdrop" onClick={() => setAdding(false)} />
          <div className="sheet">
            <div className="grip" />
            <label className="field">
              <span>Новый вид камня</span>
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)} autoFocus
                     placeholder="Например, Кунцит"
                     onKeyDown={(e) => { if (e.key === 'Enter') void addTag(); }} />
              <span className="hint">Появится в списке на всех ваших устройствах.</span>
            </label>
            <div className="dock" style={{ position: 'static', padding: '4px 0 0' }}>
              <button className="btn btn-secondary" onClick={() => setAdding(false)}>Отмена</button>
              <button className="btn btn-primary" disabled={!newTag.trim()} onClick={() => void addTag()}>
                Добавить
              </button>
            </div>
          </div>
        </>
      )}

      <div className="deck">
        <button className="tool" aria-pressed={status === 'priority'} disabled={!stoneId}
                onClick={() => void mark('priority')} aria-label="Приоритет">★</button>
        <button className="tool" aria-pressed={status === 'interesting'} disabled={!stoneId}
                onClick={() => void mark('interesting')} aria-label="Интересен">♡</button>

        <button className="trigger" data-state={mode} disabled={mode === 'working' || Boolean(error)}
                onClick={press} aria-label={hint}>
          <span className="pad" />
        </button>

        <button className="tool" disabled={!stoneId}
                onClick={() => stoneId && onOpenStone(stoneId)} aria-label="Открыть карточку">↗</button>
      </div>

      <div className="status">{hint}{frames > 1 ? ` · кадров: ${frames}` : ''}</div>

      {pricing && stoneId && (
        <PricePad defaultCurrency={exhibition.currency || 'USD'}
                  onClose={() => setPricing(false)} onSave={savePrice} />
      )}
    </div>
  );
}

/**
 * Ввод цены кнопками.
 *
 * Крупные цифры и никакой системной клавиатуры: человек в очках, одной рукой,
 * на ярком свету. Вид цены выбирается кнопкой — за карат, лотом, за всё, —
 * потому что спутать их дороже, чем ошибиться в цифре: разница бывает
 * в десятки раз.
 */
const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '000', '0', '←'];
const PAD_CURS = ['USD', 'THB', 'CNY', 'HKD', 'EUR'];
const PAD_KINDS: Array<[string, string]> = [
  ['asking_price_per_ct', 'за карат'],
  ['asking_price_per_ct_lot', 'за карат лотом'],
  ['asking_price', 'за всё'],
];

function PricePad({ defaultCurrency, onClose, onSave }: {
  /** Валюта места: в Гонконге — HKD, в Бангкоке — THB. Первая в ряду и нажата. */
  defaultCurrency: string;
  onClose: () => void;
  onSave: (attr: string, value: number, currency: string) => void;
}) {
  const [digits, setDigits] = useState('');
  const [attr, setAttr] = useState('asking_price_per_ct');
  const [cur, setCur] = useState(defaultCurrency);
  const curs = [defaultCurrency, ...PAD_CURS.filter((c) => c !== defaultCurrency)];
  const value = Number(digits || '0');

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <h2 className="t">Цена со слов поставщика</h2>
        <p className="lead">Кнопками — когда произносить вслух неудобно.</p>

        <div className="states" style={{ marginTop: 0 }}>
          {PAD_KINDS.map(([a, cap]) => (
            <button key={a} className="state-btn" aria-pressed={attr === a} onClick={() => setAttr(a)}>
              {cap}
            </button>
          ))}
        </div>

        <div className="states">
          {curs.map((c) => (
            <button key={c} className="state-btn" aria-pressed={cur === c} onClick={() => setCur(c)}>
              {c}
            </button>
          ))}
        </div>

        <div className="padout">{digits ? formatMoney(parseMoney(value, cur)!) : '0'}</div>

        <div className="pad">
          {PAD_KEYS.map((k) => (
            <button key={k} onClick={() => setDigits((d) => (
              k === '←' ? d.slice(0, -1)
                : d.length > 9 ? d
                  : (d + k).replace(/^0+(?=\d)/, '')
            ))}>{k}</button>
          ))}
        </div>

        <div className="dock" style={{ position: 'static', padding: '12px 0 0' }}>
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={!(value > 0)}
                  onClick={() => onSave(attr, value, cur)}>
            Записать
          </button>
        </div>
      </div>
    </>
  );
}
